import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core'
import { animate, state, style, transition, trigger } from '@angular/animations'
import { SelectableRow } from '@tokeet-frontend/tv3-platform'
import { TableType } from '@tv3/shared/empty-table/table-type'

import { UserStorage, isAllSelected } from '@tokeet-frontend/tv3-platform'
import * as R from 'ramda'
import {
  addRentalImagesComplete,
  Channel,
  ChannelNameTokens,
  ConfirmDialogService,
  InfoDialogService,
  Rental,
  selectChannelByName,
  selectRentalCities,
  selectRentalTags,
  Toaster,
  untilDestroy,
  updateRentalComplete,
  updateRentalDetailsComplete,
  updateRentalBaseRateComplete,
} from '@tokeet-frontend/tv3-platform'
import { MatTableDataSource } from '@angular/material/table'
import { MatPaginator } from '@angular/material/paginator'
import { MatSort } from '@angular/material/sort'
import { createSearch, isEmptyTable, localeCompareSort } from '@tokeet-frontend/tv3-platform'
import { OpenRentalOverlay } from '@tv3/store/overlay/overlay.actions'
import { RentalDialogTab } from '@tv3/containers/rentals/overlays/rental-overlay/rental-overlay.component'
import { select, Store } from '@ngrx/store'
import { SelectionModel } from '@angular/cdk/collections'
import { combineLatest } from 'rxjs'
import { startWith } from 'rxjs/operators'
import { FormControl } from '@angular/forms'
import {
  ActivateHvmiListing,
  DeactivateHvmiListing,
  DeleteHvmiListing,
  GetHvmiListings,
  getHvmiListings,
  GetHvmiRentalValidations,
  HvmiListingViewModel,
  ListingsValidationFieldResult,
  ListingValidationFieldResultItem,
  selectHvmiRentalValidations,
  selectHvmiValidationsLoaded,
} from '@tokeet-frontend/channels'
import { Actions, ofType } from '@ngrx/effects'
import { DeleteRoomComplete, UpdateRoomComplete, UpsertRoomsComplete } from '@tokeet-frontend/rentals'
import { sortBy } from 'lodash'
import { RentalValidationFixHelperService } from '../../rental-validation-fix-helper.service'
import { HvmiListingDetailsOverlayService } from '../hvmi-listing-details/hvmi-listing-details-overlay.service'
import { BookingFormulasOverlayService } from '@tv3/containers/formulas/overlay/booking-formulas-overlay.service'
import { LosDiscountTableOverlayService } from '@tv3/containers/discounts/los-discount/table-overlay/los-discount-table-overlay.service'

@Component({
  selector: 'app-connect-hvmi-wizard-step2',
  templateUrl: './connect-hvmi-wizard-step2.component.html',
  styleUrls: ['./connect-hvmi-wizard-step2.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0', visibility: 'hidden' })),
      state('expanded', style({ height: '*', visibility: 'visible' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})
export class ConnectHvmiWizardStep2Component
  extends SelectableRow<HvmiListingViewModel>
  implements OnInit, AfterViewInit
{
  @Input() channel: Channel

  @Output() prev = new EventEmitter()
  @Output() next = new EventEmitter()

  @ViewChild('paginator', { static: false }) paginator: MatPaginator
  @ViewChild(MatSort, { static: false }) sort: MatSort

  displayedColumns = [
    'select',
    'rental_name',
    'lastSync',
    'hmcId',
    'listingId',
    'hvmiStatus',
    'listingStatus',
    'action',
    'discounts',
  ]

  isExpansionDetailRow = (i: number, row: HvmiListingViewModel & { detailRow?: boolean }) => {
    return row.hasOwnProperty('detailRow')
  }
  expandedElement: any

  isEmptyTable$ = isEmptyTable(this.dataSource)

  listings: HvmiListingViewModel[]

  statusCtrl = new FormControl('')
  searchCtrl = new FormControl('')
  tagsCtrl = new FormControl([])
  citiesCtrl = new FormControl([])

  tags$ = this.store.pipe(select(selectRentalTags))
  cities$ = this.store.pipe(select(selectRentalCities))

  rentalTabs = RentalDialogTab

  searchFn = createSearch<HvmiListingViewModel>([
    'rental_name',
    'propertyId',
    'hmcId',
    'listingId',
    'hvmiStatus',
    'listingStatus',
  ])

  validations: ListingsValidationFieldResult = {}

  validationsLoaded$ = this.store.pipe(select(selectHvmiValidationsLoaded))

  constructor(
    private confirmDialog: ConfirmDialogService,
    private store: Store<any>,
    private toaster: Toaster,
    private actions: Actions,
    private losDiscountOverlay: LosDiscountTableOverlayService,
    protected storage: UserStorage,
    private listingDetailsOverlay: HvmiListingDetailsOverlayService,
    private bookingFormulasOverlay: BookingFormulasOverlayService,
    private validationFixHelper: RentalValidationFixHelperService
  ) {
    super(storage, TableType.VRBORentals)
  }

  ngOnInit() {
    this.store.dispatch(GetHvmiRentalValidations())

    this.store
      .pipe(select(selectChannelByName, { name: ChannelNameTokens.Hvmi }), untilDestroy(this))
      .subscribe((channel) => {
        this.channel = channel
      })

    this.actions
      .pipe(
        ofType(
          updateRentalComplete,
          updateRentalDetailsComplete,
          UpdateRoomComplete,
          UpsertRoomsComplete,
          addRentalImagesComplete,
          DeleteRoomComplete,
          updateRentalBaseRateComplete
        ),
        untilDestroy(this)
      )
      .subscribe(() => {
        this.store.dispatch(GetHvmiRentalValidations())
        this.store.dispatch(GetHvmiListings())
      })

    combineLatest([
      this.store.pipe(getHvmiListings),
      this.store.pipe(select(selectHvmiRentalValidations)),
      this.searchCtrl.valueChanges.pipe(startWith('')),
      this.citiesCtrl.valueChanges.pipe(startWith([])),
      this.tagsCtrl.valueChanges.pipe(startWith([])),
      this.statusCtrl.valueChanges.pipe(startWith('')),
    ])
      .pipe(untilDestroy(this))
      .subscribe(([listings, validations, term, cities, tags, status]) => {
        this.validations = validations
        this.listings = listings
        let configs = this.listings
        switch (status) {
          case 'all':
            configs = this.listings
            break
          case 'active':
            configs = R.filter(
              (config) => config.hvmiStatus === 'approved' && config.listingStatus === 'active',
              this.listings
            )
            break
          case 'inactive':
            configs = R.reject(
              (config) => config.hvmiStatus === 'approved' && config.listingStatus === 'active',
              this.listings
            )
            break
        }
        if (tags?.length > 0) {
          configs = R.filter((rental) => {
            return R.any(
              R.equals(true),
              R.map((tag) => R.contains(tag, rental.rental?.tags || []), tags)
            )
          }, configs)
        }
        if (cities?.length > 0) {
          configs = R.filter((rental) => R.includes(rental.rental?.address?.city || '', cities), configs)
        }
        if (term?.length > 0) {
          configs = this.searchFn(configs, term)
        }
        this.dataSource.data = this.withExtraRow(configs || [], this.validations)
      })
  }

  refresh() {
    this.store.dispatch(GetHvmiRentalValidations())
    this.store.dispatch(GetHvmiListings())
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator
    this.dataSource.sort = this.sort
    this.dataSource.sortData = localeCompareSort
  }

  onCancelFilters() {
    this.statusCtrl.patchValue(null)
    this.tagsCtrl.patchValue(null)
    this.citiesCtrl.patchValue(null)
    this.searchCtrl.patchValue(null)
  }

  get isFiltering() {
    return this.statusCtrl.value || this.tagsCtrl.value || this.citiesCtrl.value || this.searchCtrl.value
  }

  withExtraRow(listings: HvmiListingViewModel[], validations: ListingsValidationFieldResult) {
    listings = sortBy(listings, (listing) => validations[listing.propertyId]?.length === 0)
    return R.map((listing: HvmiListingViewModel) => {
      return {
        ...listing,
        detailRow: validations[listing.propertyId]?.length > 0,
      }
    }, listings)
  }

  onConnect() {
    if (R.any((config) => config['detailRow'], this.selection.selected as HvmiListingViewModel[])) {
      this.toaster.warning($localize`You have selected rows with invalid rentals`)
      return
    }
    R.forEach((config) => {
      // Resume Hvmi Rental
    }, this.selection.selected)

    this.onComplete()
  }

  onComplete() {
    this.next.emit(true)
  }

  allInvalid() {
    return R.all((listing) => listing['detailRow'], this.dataSource.data)
  }

  isAllSelected() {
    return isAllSelected(this.selection, this.listings, this.isRowSelectable)
  }

  isRowSelectable(listing: HvmiListingViewModel) {
    return !listing['detailRow']
  }

  setExpanded(row) {
    this.expandedElement = row
  }

  onToggleStatus(listing: HvmiListingViewModel) {
    if (listing.listingStatus === 'active') {
      this.store.dispatch(DeactivateHvmiListing({ propertyId: listing.propertyId }))
    } else {
      this.store.dispatch(ActivateHvmiListing({ propertyId: listing.propertyId }))
    }
  }

  onEditListing(listing: HvmiListingViewModel) {
    this.listingDetailsOverlay.open(listing)
  }

  onDeleteListing(listing: HvmiListingViewModel) {
    this.confirmDialog
      .confirm({
        title: $localize`Delete listing?`,
        body: $localize`Are you sure you want to delete this listing?`,
      })
      .subscribe(() => {
        this.store.dispatch(DeleteHvmiListing({ listingId: listing.listingId }))
      })
  }

  onDiscounts(listing: HvmiListingViewModel) {
    this.losDiscountOverlay.open({ channelId: this.channel.id, rentalId: listing.rental.id })
  }

  onFixRental(rental: Rental, field: ListingValidationFieldResultItem) {
    this.validationFixHelper.onFixRental(rental, field, this.validations[rental.id])
  }

  onFixRentalLatLong(rental: Rental) {
    this.store.dispatch(OpenRentalOverlay({ rental, activeTab: RentalDialogTab.Location }))
  }

  onBookingFormula() {
    this.bookingFormulasOverlay.open(this.channel)
  }
}
